import React from 'react';
import MyMaterialTable from "../mtable/MyMaterialTable";
import {formatPrice} from "../../utils/NumberFormat";
import {SalesInvoiceLineDto} from "../../model/invoice/sales/SalesInvoiceLineDto";
import {Nil} from "../../utils/Types";
import {InvoiceCalculationResult} from "../../utils/InvoiceCalculator";
import {Trans} from "react-i18next";
import {useTranslateCommon} from "../../utils/TranslationUtils";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

type IProps = {
    lines: SalesInvoiceLineDto[]
    currencyCode: string
    articleDescResolver: (code: (string | Nil)) => string | Nil
    uomNameResolver: (code: (string | Nil)) => string | Nil
    taxNameResolver: (code: (string | Nil)) => string | Nil
    calculationResults: InvoiceCalculationResult
}

const useStyles = makeStyles(() =>
        createStyles({
            invoice_line_description: {
                margin: 'unset',
                fontFamily: 'unset',
                display: 'unset',
            },
        }),
);

export const InvoiceLinesTable = (
        {lines, currencyCode, articleDescResolver, uomNameResolver, taxNameResolver, calculationResults}: IProps) => {
    const showDiscountColumn = Boolean(lines.find(it => it.discountPct))
    const showUnitColumn = Boolean(lines.find(it => it.item.uomCode))
    const showQuantityColumn = showUnitColumn ? true : Boolean(lines.find(it => it.quantity && it.quantity > 1))
    const showTotalExclVatColumn = showUnitColumn || showQuantityColumn;

    const t = useTranslateCommon();
    const classes = useStyles();

    return <MyMaterialTable
            style={{
                width: "100%",
                position: "unset",
                boxShadow: "unset"
            }}
            options={{
                search: false,
                paging: false,
                sorting: false,
                showTitle: false,
                toolbar: false,
                draggable: false,
                // @ts-ignore
                cellStyle: {padding: '0.3em'},
                headerStyle: {padding: '0.3em'},
            }}
            columns={[
                {
                    title: <Trans i18nKey="single_invoice.table.description">Description</Trans>,
                    align: "left",
                    headerStyle: {textAlign: "center"},
                    render: row => <pre className={classes.invoice_line_description}>
                                {t("common.article", row.item?.description ?? articleDescResolver(row.item?.code))}
                            </pre>
                },
                {
                    title: <Trans i18nKey="single_invoice.table.quantity">Quantity</Trans>,
                    align: "center",
                    width: "1%",
                    hidden: !showQuantityColumn,
                    render: row => row.quantity,
                },
                {
                    title: <Trans i18nKey="single_invoice.table.unit">Unit</Trans>,
                    align: "center",
                    hidden: !showUnitColumn,
                    render: row => t("common.uom", row.item.uomCode ? uomNameResolver(row.item.uomCode) : '')
                },
                {
                    title: <Trans i18nKey="single_invoice.table.price">Price</Trans>,
                    align: "center",
                    width: "5%",
                    render: row => formatPrice(row.price, currencyCode)
                },
                {
                    title: <Trans i18nKey="single_invoice.table.disc_pct">Disc. %</Trans>,
                    align: "center",
                    width: "1%",
                    hidden: !showDiscountColumn,
                    render: (row) => row.discountPct ? row.discountPct : ''
                },
                {
                    title: <Trans i18nKey="single_invoice.table.total_no_vat">Total Excl. VAT</Trans>,
                    align: "center",
                    width: "5%",
                    hidden: !showTotalExclVatColumn,
                    render: row => formatPrice(calculationResults.byLine(row).totalExclVat, currencyCode)
                },
                {
                    title: <Trans i18nKey="single_invoice.table.vat">VAT</Trans>,
                    align: "center",
                    width: "5%",
                    render: row => t("common.tax", taxNameResolver(row.taxCode))
                },
                {
                    title: <Trans i18nKey="single_invoice.table.total">Total</Trans>,
                    align: "center",
                    width: "5%",
                    render: row => formatPrice(calculationResults.byLine(row).total, currencyCode)
                }
            ]}
            data={lines}
    />;

}
